import { useMemo, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AsyncState } from '@/components/async';
import { Skeleton } from '@/components/feedback';
import { StatusBadge, type BadgeTone } from '@/components/dashboard/StatusBadge';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';
import { listShowings, type ShowingWithLead } from '@/lib/services/showings';
import type { ShowingStatus } from '@/lib/types/showing';
import { formatUnitLabel, humanizeAddressText } from '@/lib/utils/propertyFormatting';
import { deriveShowingSlotTimestamp, formatShowingSlotLabel } from '@/lib/utils/showingsFormatting';

const SHOWING_LABELS: Record<ShowingStatus, string> = {
  requested: 'Requested',
  confirmed: 'Confirmed',
  rescheduled: 'Rescheduled',
  cancelled: 'Cancelled',
};

const SHOWINGS_SKELETON = (
  <div className="flex flex-col gap-3" aria-label="Loading showings">
    <Skeleton className="h-20 w-full rounded-2xl" />
    <Skeleton className="h-20 w-full rounded-2xl" />
    <Skeleton className="h-20 w-full rounded-2xl" />
  </div>
);

interface LeadShowingRow {
  id: string;
  status: ShowingStatus;
  slotLabel: string;
  unitLabel: string;
  message: string | null;
  timestamp: number | null;
}

export function LeadShowingsPanel({
  leadId,
  leadIds,
}: {
  leadId?: string;
  leadIds?: string[];
}): JSX.Element | null {
  const user = useDashboardUser();
  const resolvedLeadIds = useMemo(() => {
    const sources = Array.isArray(leadIds) && leadIds.length > 0 ? leadIds : leadId ? [leadId] : [];
    const normalized = sources.map((value) => value.trim()).filter((value) => value.length > 0);
    const unique = Array.from(new Set(normalized));
    unique.sort();
    return unique;
  }, [leadId, leadIds]);

  const showingsQuery = useQuery<ShowingWithLead[]>({
    queryKey: ['dashboard-showings', { view: 'lead-detail', leadIds: resolvedLeadIds }],
    enabled: resolvedLeadIds.length > 0,
    queryFn: async ({ signal }) => {
      const responses = await Promise.all(
        resolvedLeadIds.map(async (targetLeadId) => {
          return await listShowings({
            user,
            filters: { leadId: targetLeadId },
            signal,
          });
        }),
      );

      const deduped = new Map<string, ShowingWithLead>();
      responses.flat().forEach((entry) => {
        deduped.set(entry.showing.id, entry);
      });

      return Array.from(deduped.values());
    },
  });

  const rows = useMemo((): LeadShowingRow[] => {
    const entries = showingsQuery.data ?? [];
    const now = Date.now();

    const mapped = entries.map((entry) => {
      const showing = entry.showing;
      const scheduledAt = toNonEmptyString(showing.scheduled_at);
      const preferredDate = toNonEmptyString(showing.preferred_date);
      const preferredTime = toNonEmptyString(showing.preferred_time);
      const timestamp = deriveShowingSlotTimestamp({ scheduledAt, preferredDate, preferredTime });

      return {
        id: showing.id,
        status: showing.status as ShowingStatus,
        slotLabel: formatShowingSlotLabel({ scheduledAt, preferredDate, preferredTime }),
        unitLabel: formatShowingUnitLabel(entry),
        message: toNonEmptyString(showing.message),
        timestamp,
      };
    });

    return mapped.sort((a, b) => compareShowings(a, b, now));
  }, [showingsQuery.data]);

  if (resolvedLeadIds.length === 0) {
    return null;
  }

  const relatedLeadLabel =
    resolvedLeadIds.length > 1
      ? `Matched ${resolvedLeadIds.length.toString()} lead records by phone.`
      : null;

  return (
    <section className="rounded-3xl border border-surface-subtle bg-white p-8 shadow-card">
      <header className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-1">
          <h3 className="text-lg font-semibold text-text-primary">Showings</h3>
          <p className="text-sm text-text-primary/70">Requests, confirmations, and scheduling.</p>
          {relatedLeadLabel ? (
            <p className="text-xs text-text-primary/60">{relatedLeadLabel}</p>
          ) : null}
        </div>
        <StatusBadge label={`${rows.length.toString()} total`} tone="neutral" />
      </header>

      <div className="mt-6">
        <AsyncState
          data={showingsQuery.data ? rows : null}
          isLoading={showingsQuery.isLoading || showingsQuery.isFetching}
          error={showingsQuery.error}
          skeleton={SHOWINGS_SKELETON}
        >
          {(items) =>
            items.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-surface-subtle bg-surface-light px-6 py-8 text-sm text-text-primary/70">
                No showings requested yet.
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {items.map((showing) => (
                  <article
                    key={showing.id}
                    className="flex flex-col gap-3 rounded-2xl border border-surface-subtle bg-surface-light px-5 py-4"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex flex-col gap-1">
                        <p className="text-sm font-semibold text-text-primary">
                          {showing.slotLabel}
                        </p>
                        <p className="text-xs text-text-primary/70">{showing.unitLabel}</p>
                      </div>
                      <StatusBadge
                        label={SHOWING_LABELS[showing.status]}
                        tone={toneForShowingStatus(showing.status)}
                      />
                    </div>
                    {typeof showing.message === 'string' && showing.message.length > 0 ? (
                      <p className="whitespace-pre-line text-sm text-text-primary/80">
                        {showing.message}
                      </p>
                    ) : null}
                  </article>
                ))}
              </div>
            )
          }
        </AsyncState>
      </div>
    </section>
  );
}

function toneForShowingStatus(status: ShowingStatus): BadgeTone {
  switch (status) {
    case 'confirmed':
      return 'success';
    case 'rescheduled':
      return 'warning';
    case 'cancelled':
      return 'danger';
    default:
      return 'info';
  }
}

function statusPriority(status: ShowingStatus): number {
  switch (status) {
    case 'confirmed':
    case 'rescheduled':
      return 0;
    case 'requested':
      return 1;
    case 'cancelled':
      return 2;
    default:
      return 3;
  }
}

function compareShowings(a: LeadShowingRow, b: LeadShowingRow, now: number): number {
  const priorityDiff = statusPriority(a.status) - statusPriority(b.status);
  if (priorityDiff !== 0) return priorityDiff;

  const aTimestamp = a.timestamp;
  const bTimestamp = b.timestamp;
  const aHasTimestamp = typeof aTimestamp === 'number' && Number.isFinite(aTimestamp);
  const bHasTimestamp = typeof bTimestamp === 'number' && Number.isFinite(bTimestamp);

  if (!aHasTimestamp && !bHasTimestamp) {
    return 0;
  }
  if (!aHasTimestamp) return 1;
  if (!bHasTimestamp) return -1;

  const aIsPast = aTimestamp < now;
  const bIsPast = bTimestamp < now;

  if (aIsPast !== bIsPast) {
    return aIsPast ? 1 : -1;
  }

  if (!aIsPast && !bIsPast) {
    return aTimestamp - bTimestamp;
  }

  return bTimestamp - aTimestamp;
}

function toNonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function formatShowingUnitLabel(entry: ShowingWithLead): string {
  const unit = entry.unit;
  const unitDisplay = unit ? humanizeAddressText(formatUnitLabel(unit)) : '';
  const propertyCode = unit ? humanizeAddressText(unit.propertyCode) : '';
  const hasUnitMeta = unitDisplay.trim().length > 0 && propertyCode.trim().length > 0;
  return hasUnitMeta
    ? `${unitDisplay} · ${propertyCode}`
    : (entry.showing.unit_id ?? 'Unit not set');
}
